import { PathwayConstants } from './constants/pathway/pathway.constants';

export interface KcPollingContext {
  practiceCallUuid: string;
  isPathwayFlow?: boolean;
  stepName?: string;
}

export class PathwayLocalStorageUtil {
  private static readonly CONFETTI_SHOWN_KEY = 'pathway_confetti_shown';

  static getConfettiShownUuids(): string[] {
    try {
      const stored = localStorage.getItem(this.CONFETTI_SHOWN_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  static hasConfettiBeenShown(enrollmentUuid: string): boolean {
    return this.getConfettiShownUuids().includes(enrollmentUuid);
  }

  static markConfettiAsShown(enrollmentUuid: string): void {
    const shown = this.getConfettiShownUuids();
    if (!shown.includes(enrollmentUuid)) {
      shown.push(enrollmentUuid);
      localStorage.setItem(this.CONFETTI_SHOWN_KEY, JSON.stringify(shown));
    }
  }

  static getAssessmentExitAnimate(): boolean {
    return localStorage.getItem(PathwayConstants.ASSESSMENT_EXIT_ANIMATE_KEY) === 'true';
  }

  static setAssessmentExitAnimate(): void {
    localStorage.setItem(PathwayConstants.ASSESSMENT_EXIT_ANIMATE_KEY, 'true');
  }

  static clearAssessmentExitAnimate(): void {
    localStorage.removeItem(PathwayConstants.ASSESSMENT_EXIT_ANIMATE_KEY);
  }

  static getAssessmentPersonalizationError(): boolean {
    return localStorage.getItem(PathwayConstants.ASSESSMENT_PERSONALIZATION_ERROR_KEY) === 'true';
  }

  static setAssessmentPersonalizationError(): void {
    localStorage.setItem(PathwayConstants.ASSESSMENT_PERSONALIZATION_ERROR_KEY, 'true');
  }

  static clearAssessmentPersonalizationError(): void {
    localStorage.removeItem(PathwayConstants.ASSESSMENT_PERSONALIZATION_ERROR_KEY);
  }

  static getAssessmentPracticeCallUuid(): string | null {
    return localStorage.getItem(PathwayConstants.ASSESSMENT_PRACTICE_CALL_UUID_KEY);
  }

  static setAssessmentPracticeCallUuid(uuid: string): void {
    if (uuid) {
      localStorage.setItem(PathwayConstants.ASSESSMENT_PRACTICE_CALL_UUID_KEY, uuid);
    }
  }

  static clearAssessmentPracticeCallUuid(): void {
    localStorage.removeItem(PathwayConstants.ASSESSMENT_PRACTICE_CALL_UUID_KEY);
  }

  private static readonly ROLEPLAY_RESULTS_PENDING_KEY = 'pathway_results_pending_call_uuid';
  private static readonly QUIZ_RESULTS_PENDING_KEY = 'pathway_quiz_results_pending_call_uuid';

  static getRoleplayPendingUuid(): string | null {
    try { return localStorage.getItem(this.ROLEPLAY_RESULTS_PENDING_KEY); } catch { return null; }
  }

  static markRoleplayPending(uuid: string): void {
    try { localStorage.setItem(this.ROLEPLAY_RESULTS_PENDING_KEY, uuid); } catch {}
  }

  static clearRoleplayPending(): void {
    try { localStorage.removeItem(this.ROLEPLAY_RESULTS_PENDING_KEY); } catch {}
  }

  static getQuizPendingUuid(): string | null {
    try { return localStorage.getItem(this.QUIZ_RESULTS_PENDING_KEY); } catch { return null; }
  }

  static markQuizPending(uuid: string): void {
    try { localStorage.setItem(this.QUIZ_RESULTS_PENDING_KEY, uuid); } catch {}
  }

  static clearQuizPending(): void {
    try { localStorage.removeItem(this.QUIZ_RESULTS_PENDING_KEY); } catch {}
  }

  private static readonly ROLEPLAY_EXITED_KEY = 'pathway_roleplay_exited_call_uuid';

  static getRoleplayExitedUuid(): string | null {
    try { return localStorage.getItem(this.ROLEPLAY_EXITED_KEY); } catch { return null; }
  }

  static markRoleplayExited(uuid: string): void {
    try { localStorage.setItem(this.ROLEPLAY_EXITED_KEY, uuid); } catch {}
  }

  static clearRoleplayExited(uuid?: string): void {
    try {
      if (!uuid || localStorage.getItem(this.ROLEPLAY_EXITED_KEY) === uuid) {
        localStorage.removeItem(this.ROLEPLAY_EXITED_KEY);
      }
    } catch {}
  }

  private static readonly KC_POLLING_CONTEXT_KEY = 'kc_results_polling_context';

  static setKcPollingContext(ctx: KcPollingContext): void {
    try { localStorage.setItem(this.KC_POLLING_CONTEXT_KEY, JSON.stringify(ctx)); } catch {}
  }

  static getKcPollingContext(): KcPollingContext | null {
    try {
      const stored = localStorage.getItem(this.KC_POLLING_CONTEXT_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  }

  static clearKcPollingContext(): void {
    try { localStorage.removeItem(this.KC_POLLING_CONTEXT_KEY); } catch {}
  }
}
